import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Trophy, Sparkles, TrendingUp, Target, AlertTriangle, ArrowUp, ArrowDown,
  Minus, Crown, ArrowUpDown, Gift,
} from 'lucide-react';
import { MiniRing } from './ScoreRing';
import {
  C, fontSerif, fontMono, bandFor, fmtNum, currencySymbol,
} from './utils';

const ICONS = { Trophy, Sparkles, TrendingUp, Target, AlertTriangle };

const RANK_ACCENT = ['#D4AF37', '#9CA3AF', '#B08D57'];

const EmployeeCard = ({ card, rank, onOpen, delay }) => {
  const band = bandFor(card.score_band, card.reward_status);
  const BandIcon = ICONS[band.icon] || Target;
  const change = card.change_since_prev;
  const today = card.today || {};
  const todayPct = today.target > 0 ? Math.min((today.completed / today.target) * 100, 100) : 0;
  const monthPct = card.month_effective_target > 0
    ? Math.min((card.month_actual / card.month_effective_target) * 100, 110)
    : 0;
  const isTop = rank === 0 && card.overall_score != null;

  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      onClick={() => onOpen(card.employee_id)}
      className="group relative w-full overflow-hidden rounded-2xl border bg-white p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_44px_-14px_rgba(139,30,63,0.22)]"
      style={{ borderColor: isTop ? C.gold : C.border }}
      data-testid={`employee-card-${card.employee_id}`}
    >
      {isTop && (
        <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-15"
          style={{ background: `radial-gradient(circle, ${C.gold}, transparent 70%)` }} />
      )}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold tabular-nums text-white"
            style={{ fontFamily: fontMono, background: RANK_ACCENT[rank] || C.text3 }}
          >
            {rank + 1}
          </span>
          <div>
            <div className="flex items-center gap-1.5">
              <h4 className="text-base font-semibold" style={{ color: C.text }}>{card.name}</h4>
              {card.is_manager && <Crown className="h-3.5 w-3.5" style={{ color: C.gold }} />}
            </div>
            <p className="text-xs text-slate-400">{card.role_title}</p>
          </div>
        </div>
        <MiniRing score={card.overall_score} color={band.ring} testId={`mini-ring-${card.employee_id}`} />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${band.badge}`}>
          <BandIcon className="h-3.5 w-3.5" />
          {card.reward_status === 'DISQUALIFIED' ? 'Gate Blocked' : band.label}
        </span>
        {change != null && change !== 0 && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${change > 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
            {change > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            {fmtNum(Math.abs(change), 1)}
          </span>
        )}
        {change === 0 && (
          <span className="inline-flex items-center gap-0.5 text-xs text-slate-400"><Minus className="h-3 w-3" /> flat</span>
        )}
      </div>

      {/* dual progress */}
      <div className="mt-4 space-y-3">
        <div>
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-medium text-teal-700">Monthly output</span>
            <span className="tabular-nums text-slate-500" style={{ fontFamily: fontMono }}>
              {fmtNum(card.month_actual)}/{fmtNum(card.month_effective_target)}
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-teal-50">
            <div className="h-full rounded-full" style={{ width: `${monthPct}%`, background: C.teal }} />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-medium text-indigo-700">Daily adherence</span>
            <span className="tabular-nums text-slate-500" style={{ fontFamily: fontMono }}>
              {card.daily_adherence_pct == null ? '—' : `${fmtNum(card.daily_adherence_pct, 0)}%`}
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-indigo-50">
            <div className="h-full rounded-full" style={{ width: `${card.daily_adherence_pct || 0}%`, background: C.indigo }} />
          </div>
        </div>
      </div>

      {today.is_working_day && (
        <p className="mt-3 text-xs text-slate-400">
          Today: <span className="font-semibold text-slate-600" style={{ fontFamily: fontMono }}>{fmtNum(today.completed)}/{fmtNum(today.target)}</span> {todayPct >= 100 ? '✓' : ''}
        </p>
      )}
    </motion.button>
  );
};

export const TeamBoardView = ({ board, onOpenEmployee }) => {
  const [sortBy, setSortBy] = useState('score');
  if (!board) return null;

  const cards = [...(board.cards || [])];
  if (sortBy === 'name') {
    cards.sort((a, b) => a.name.localeCompare(b.name));
  } else {
    cards.sort((a, b) => (b.overall_score ?? -1) - (a.overall_score ?? -1));
  }

  const sym = currencySymbol(board.settings?.currency);
  const unlockedCount = (board.cards || []).filter((c) => c.reward_status === 'UNLOCKED').length;

  return (
    <div className="space-y-6" data-testid="team-board-view">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold" style={{ fontFamily: fontSerif, color: C.text }}>Company Board</h2>
          <p className="text-sm text-slate-500">
            {unlockedCount > 0
              ? `${unlockedCount} teammate${unlockedCount > 1 ? 's have' : ' has'} unlocked the reward so far 🎉`
              : `Race to the ${sym}${board.settings?.reward_amount?.toLocaleString('en-IN') || ''} ${board.settings?.reward_name || 'reward'}`}
          </p>
        </div>
        <button
          onClick={() => setSortBy((s) => (s === 'score' ? 'name' : 'score'))}
          className="inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
          style={{ borderColor: C.border }}
          data-testid="leaderboard-sort-toggle"
        >
          <ArrowUpDown className="h-3.5 w-3.5" />
          Sort by {sortBy === 'score' ? 'score' : 'name'}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="employee-card-list">
        {cards.map((card, i) => (
          <EmployeeCard key={card.employee_id} card={card} rank={i} onOpen={onOpenEmployee} delay={i * 0.06} />
        ))}
      </div>
    </div>
  );
};
